import {
  SETUP_SERVICE_PRICE_DISPLAY,
  SETUP_SERVICE_PRICE_NUMERIC,
  SETUP_SERVICE_PRICE_PERIOD,
} from "@/lib/setup-service-copy";

// No hardcoded price-ID fallbacks (review ACQ-9): the old placeholder IDs
// belonged to a different Stripe account, so a build with missing envs
// produced CTAs that 400 server-side with a generic error. An empty ID
// fails the server allowlist just the same, but is greppable and honest.
const STRIPE_PRICE_MONTHLY =
  process.env.NEXT_PUBLIC_STRIPE_PRICE_MONTHLY ?? "";
const STRIPE_PRICE_ANNUAL =
  process.env.NEXT_PUBLIC_STRIPE_PRICE_ANNUAL ?? "";
const STRIPE_PRICE_BUSINESS_MONTHLY =
  process.env.NEXT_PUBLIC_STRIPE_PRICE_BUSINESS_MONTHLY ??
  process.env.STRIPE_PRICE_BUSINESS_MONTHLY ??
  "";
const STRIPE_PRICE_SITE_CONTROL_MONTHLY =
  process.env.NEXT_PUBLIC_STRIPE_PRICE_SITE_CONTROL_MONTHLY ??
  process.env.STRIPE_PRICE_SITE_CONTROL_MONTHLY ??
  "";

const SITE_CONTROL_STANDARD_PRICE = 395;
const SITE_CONTROL_FOUNDING_PRICE = 295;
const SITE_CONTROL_FOUNDING_OPEN: boolean = true;

export const PRICING = {
  monthly: {
    price: 39,
    display: "\u00a339",
    displayWithPeriod: "\u00a339/mo",
    stripe: {
      priceId: STRIPE_PRICE_MONTHLY,
    },
  },
  annual: {
    price: 360,
    pricePerMonth: 30,
    display: "\u00a330",
    displayWithPeriod: "\u00a330/mo billed yearly",
    displayYearly: "\u00a3360/yr",
    stripe: {
      priceId: STRIPE_PRICE_ANNUAL,
    },
  },
  savings: {
    // Monthly x 12 minus the annual price. No "N months free" figure: £108 is
    // about 2.8 months of the monthly price, so "2 months free" understated it
    // and any rounded month claim drifts when a price changes.
    perYear: 108,
    display: "Save \u00a3108/year",
    percentage: "23%",
  },
  free: {
    price: 0,
    display: "\u00a30",
  },
  // Advanced — £99/mo, monthly only (spec v1.2). No Stripe price yet: the
  // checkout mapping lands with the billing PR, so it cannot be purchased.
  advanced: {
    price: 99,
    display: "£99",
    displayWithPeriod: "£99/mo",
  },
  business: {
    price: 199,
    display: "\u00a3199",
    displayWithPeriod: "\u00a3199/mo",
    stripe: {
      priceId: STRIPE_PRICE_BUSINESS_MONTHLY,
    },
  },
  // Site Control \u2014 per-site enterprise tier, publicly LAUNCHED (GA). The
  // `site_control` flag is a kill-switch (isSiteControlLive), not a launch gate.
  // STRIPE_PRICE_SITE_CONTROL_MONTHLY drives provisioning after a sales-led sale;
  // it does not gate the marketing surface.
  //
  // One flat per-site price: no setup fee, no volume tiers, month to month,
  // unlimited workers. While `founding.open`, the ONE live Stripe price is the
  // founding price (first five principal contractors, locked for two years);
  // once the fifth signs, set `founding.open: false`, point
  // STRIPE_PRICE_SITE_CONTROL_MONTHLY at a new standard-price Stripe price and
  // move the founding price ID into STRIPE_PRICE_SITE_CONTROL_ACCEPTED so the
  // founding subscriptions keep classifying as Site Control.
  // Runbook: docs/site-control-operator-handoff.md.
  siteControl: {
    price: SITE_CONTROL_STANDARD_PRICE,
    display: `\u00a3${SITE_CONTROL_STANDARD_PRICE}`,
    displayWithPeriod: `\u00a3${SITE_CONTROL_STANDARD_PRICE}/site/mo`,
    founding: {
      open: SITE_CONTROL_FOUNDING_OPEN,
      price: SITE_CONTROL_FOUNDING_PRICE,
      display: `\u00a3${SITE_CONTROL_FOUNDING_PRICE}`,
      displayWithPeriod: `\u00a3${SITE_CONTROL_FOUNDING_PRICE}/site/mo`,
      places: 5,
      lockYears: 2,
    },
    /** The per-site price the live Stripe price must charge right now. */
    currentPrice: SITE_CONTROL_FOUNDING_OPEN
      ? SITE_CONTROL_FOUNDING_PRICE
      : SITE_CONTROL_STANDARD_PRICE,
    stripe: {
      priceId: STRIPE_PRICE_SITE_CONTROL_MONTHLY,
    },
  },
  setupService: {
    price: SETUP_SERVICE_PRICE_NUMERIC,
    display: SETUP_SERVICE_PRICE_DISPLAY,
    displayWithPeriod: `${SETUP_SERVICE_PRICE_DISPLAY} ${SETUP_SERVICE_PRICE_PERIOD}`,
  },
} as const;
